import {Badge} from '@mantine/core';
import {Card} from 'src/design/atoms/Card';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  IconAlertCircleFilled,
  IconCircleCheckFilled,
  IconInfoCircleFilled,
} from '@tabler/icons-react';
import React from 'react';
import type {HierarchyTreeNode} from 'src/design/organisms/HierarchyTree';
import {TreeBrowserPane} from 'src/design/organisms/TreeBrowserPane';
import type {DeviceGroupNode, DeviceRollupCounts} from './deviceRollup';
import {ALL_LOCATIONS_NODE_ID} from './deviceRollup';

export type LocationTreeLegend = 'inline' | 'card' | 'none';

export interface LocationTreeProps {
  tree: DeviceGroupNode[];
  totalCount: number;
  selectedNodeId: string;
  onSelectNode: (id: string) => void;
  /** Legend placement: 'inline' (default) badge row above the tree, 'card' bordered legend
   *  below the tree, 'none' to hide it. */
  legend?: LocationTreeLegend;
}

type RollupStatus = 'attention' | 'partial' | 'skipped' | 'clear';

// Worst-first tier resolution: `failed` is unambiguously worst; between noAccess (explicit
// "couldn't access") and truly-unentered, noAccess outranks. A roll-up summary, not an average.
const resolveRollupStatus = (counts: DeviceRollupCounts): RollupStatus => {
  if (counts.failed > 0) return 'attention';
  if (counts.noAccess > 0) return 'skipped'; // explicit "couldn't access" outcome — not unentered
  if (counts.skipped > 0) return 'partial'; // truly unentered remainder — group still in progress
  return 'clear'; // failed=0, noAccess=0, skipped=0 → everything is Pass
};

interface RollupStatusConfig {
  icon: () => React.ReactNode;
  count?: (counts: DeviceRollupCounts) => number;
  label: (counts: DeviceRollupCounts) => string;
  legendLabel: string;
}

// Status vocabulary shared between the per-node roll-up dots and the 'card' legend below:
// attention=failed, skipped=noAccess (explicit outcome), partial=truly-unentered remainder.
const ROLLUP_STATUS_CONFIG: Record<RollupStatus, RollupStatusConfig> = {
  attention: {
    icon: () => (
      <IconAlertCircleFilled
        size={16}
        color="var(--mantine-color-red-6)"
        aria-hidden="true"
      />
    ),
    count: counts => counts.failed,
    label: counts =>
      `${counts.failed} device${counts.failed === 1 ? '' : 's'} failed`,
    legendLabel: 'Failed devices',
  },
  skipped: {
    icon: () => (
      <IconInfoCircleFilled
        size={16}
        color="var(--mantine-color-blue-6)"
        aria-hidden="true"
      />
    ),
    count: counts => counts.noAccess,
    label: counts =>
      `${counts.noAccess} device${counts.noAccess === 1 ? '' : 's'} no access`,
    legendLabel: 'Skipped devices',
  },
  partial: {
    icon: () => (
      <IconAlertCircleFilled
        size={16}
        color="var(--mantine-color-yellow-6)"
        aria-hidden="true"
      />
    ),
    count: counts => counts.skipped,
    label: counts =>
      `${counts.skipped} device${
        counts.skipped === 1 ? '' : 's'
      } not yet inspected`,
    legendLabel: 'Caution / Partial',
  },
  clear: {
    icon: () => (
      <IconCircleCheckFilled
        size={16}
        color="var(--mantine-color-green-6)"
        aria-hidden="true"
      />
    ),
    label: () => 'All devices inspected',
    legendLabel: 'All inspected',
  },
};

// Legend rows read worst-to-best, top to bottom, mirroring `resolveRollupStatus`'s priority order.
const LEGEND_ORDER: RollupStatus[] = [
  'clear',
  'skipped',
  'partial',
  'attention',
];

const RollupBadge: React.FC<{node: DeviceGroupNode}> = ({node}) => {
  const status = resolveRollupStatus(node.counts);
  const config = ROLLUP_STATUS_CONFIG[status];
  const count = config.count?.(node.counts);
  return (
    <Group
      gap={4}
      wrap="nowrap"
      role="img"
      aria-label={config.label(node.counts)}>
      {count !== undefined && (
        <Text size="xs" c="dimmed" aria-hidden="true">
          {count}
        </Text>
      )}
      {config.icon()}
    </Group>
  );
};

const toTreeNodes = (nodes: DeviceGroupNode[]): HierarchyTreeNode[] =>
  nodes.map(node => ({
    id: node.key,
    label: node.label,
    // No `count` here — RollupBadge's own count (failed/skipped) replaces the
    // generic total for Building/Floor/Room rows.
    adornment: <RollupBadge node={node} />,
    children: node.children ? toTreeNodes(node.children) : undefined,
  }));

// Building -> Floor -> Room location picker for the Devices tab. The "All
// locations" root is supplied here (HierarchyTree has no built-in root).
export const LocationTree: React.FC<LocationTreeProps> = ({
  tree,
  totalCount,
  selectedNodeId,
  onSelectNode,
  legend = 'inline',
}) => {
  const nodes: HierarchyTreeNode[] = [
    {id: ALL_LOCATIONS_NODE_ID, label: 'All locations', count: totalCount},
    ...toTreeNodes(tree),
  ];

  const inlineLegend = (
    <Group gap="xs">
      <Badge size="sm" color="gray" variant="filled">
        Not Inspected
      </Badge>
      <Badge size="sm" color="red" variant="filled">
        Failed
      </Badge>
      <Badge size="sm" color="yellow" variant="filled">
        Partial
      </Badge>
      <Text size="xs" c="dimmed">
        — group roll-up legend
      </Text>
    </Group>
  );

  const cardLegend = (
    <Card withBorder padding="sm" aria-label="Location status legend">
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">
        Location status
      </Text>
      <Stack gap={6}>
        {LEGEND_ORDER.map(status => (
          <Group gap={6} key={status}>
            {ROLLUP_STATUS_CONFIG[status].icon()}
            <Text size="xs">{ROLLUP_STATUS_CONFIG[status].legendLabel}</Text>
          </Group>
        ))}
      </Stack>
    </Card>
  );

  return (
    <TreeBrowserPane
      title="Location"
      nodes={nodes}
      selectedId={selectedNodeId}
      onSelect={onSelectNode}
      defaultExpandedIds={tree.map(node => node.key)}
      header={legend === 'inline' ? inlineLegend : undefined}
      footer={legend === 'card' ? cardLegend : undefined}
    />
  );
};
